/**
 * Created by katarinababic on 23.4.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useCallback, useState } from 'react';
import { ExerciseImage } from './ExerciseImage';
import styled from '@emotion/styled';
import { Theme } from '../../../Theme/Theme';
import { ViewPhase } from '../../../Model/WorkoutLog/ViewPhase';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { ViewBlock } from '../../../Model/WorkoutLog/ViewBlock';
import { useLanguage } from '../../../Hooks/useLanguage';
import { ExerciseCardSetCounter } from './ExerciseCardSetCounter';
import { ExerciseVariation } from '../../../Model/ProgramPortfolio/ExerciseVariation';
import { ExerciseVideoModal } from '../../ExploreCards/ExerciseVideoModal';

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${Theme.Colors.lighterGrey};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 4px;
    height: 18px;
    border-radius: 2px;
    background: linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.secondary});
  }
`;

const SectionSubtitle = styled.span`
  font-size: 13px;
  color: ${Theme.Colors.warmGrey};
  font-weight: 500;
`;

const ExerciseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ExerciseItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: ${Theme.Colors.white};
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 16px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${Theme.Colors.primary}40;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  &:hover .play-hint {
    opacity: 1;
  }
`;

const PlayHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${Theme.Colors.primary}15;
  color: ${Theme.Colors.primary};
  font-size: 16px;
  margin-left: 12px;
  flex-shrink: 0;
  opacity: 0.6;
  transition: opacity 0.2s ease;
`;

const ExerciseInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ExerciseTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  line-height: 1.3;
`;

const ExerciseMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const MetaBadge = styled.span<{ $variant?: 'primary' | 'secondary' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $variant }) =>
    $variant === 'primary'
      ? `${Theme.Colors.primary}15`
      : $variant === 'secondary'
      ? `${Theme.Colors.secondary}15`
      : Theme.Colors.lightestGrey};
  color: ${({ $variant }) =>
    $variant === 'primary'
      ? Theme.Colors.primary
      : $variant === 'secondary'
      ? Theme.Colors.secondaryDark
      : Theme.Colors.charcoal};

  i {
    font-size: 12px;
  }
`;

const ExerciseIndex = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${Theme.Colors.primary}20, ${Theme.Colors.secondary}20);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: ${Theme.Colors.primary};
  margin-right: 14px;
  flex-shrink: 0;
`;

const BodyPartTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const BodyPartTag = styled.span`
  font-size: 11px;
  color: ${Theme.Colors.warmGrey};
  background: ${Theme.Colors.lightestGrey};
  padding: 2px 8px;
  border-radius: 8px;
`;

export type WorkoutTemplatePhaseProps = {
  phase: ViewPhase;
  data: ViewBlock[];
};

export const WorkoutTemplatePhase: React.FC<WorkoutTemplatePhaseProps> = observer(({ phase, data }) => {
  const { t } = useTrans();
  const { language } = useLanguage();

  const [selectedExercise, setSelectedExercise] = useState<ExerciseVariation | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleExerciseClick = useCallback((exercise: ExerciseVariation) => {
    setSelectedExercise(exercise);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedExercise(null);
  }, []);

  const phaseTitle = phase.type ? t(`phase.${phase.type}`) : t('phase.unplanned');
  const exerciseCount = data.length;
  const subtitle = t('workoutViewScreen.phases.exerciseCount', {
    defaultValue: '{{count}} Exercises',
    count: exerciseCount,
  });

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{phaseTitle}</SectionTitle>
        <SectionSubtitle>{subtitle}</SectionSubtitle>
      </SectionHeader>
      <ExerciseList>
        {data.map((viewBlock, index) => {
          const exercise = viewBlock.exercise;
          const setsCount = viewBlock.plannedSetsCount;
          const firstSet = viewBlock.sets[0];
          const isDurationBased = firstSet?.durationBased ?? false;
          const bodyParts = exercise.bodyParts?.slice(0, 2) ?? [];
          const equipmentTypes = exercise.equipmentTypes?.slice(0, 2) ?? [];

          return (
            <ExerciseItem key={viewBlock.viewBlockId} onClick={() => handleExerciseClick(exercise)}>
              <ExerciseIndex>{index + 1}</ExerciseIndex>
              <ExerciseImage viewBlock={viewBlock} />
              <ExerciseInfo>
                <ExerciseTitle>{exercise.localeName(language)}</ExerciseTitle>
                <ExerciseMeta>
                  <MetaBadge $variant="primary">
                    <i className="bi bi-layers" />
                    {setsCount} {setsCount === 1 ? t('globals.set', { defaultValue: 'set' }) : t('globals.sets', { defaultValue: 'sets' })}
                  </MetaBadge>
                  {isDurationBased ? (
                    <MetaBadge $variant="secondary">
                      <i className="bi bi-stopwatch" />
                      {t('exercise.timeBased', { defaultValue: 'Time' })}
                    </MetaBadge>
                  ) : (
                    <MetaBadge $variant="secondary">
                      <i className="bi bi-arrow-repeat" />
                      {t('exercise.repsBased', { defaultValue: 'Reps' })}
                    </MetaBadge>
                  )}
                  {equipmentTypes.length > 0 && (
                    <MetaBadge>
                      <i className="bi bi-tools" />
                      {equipmentTypes[0]}
                    </MetaBadge>
                  )}
                </ExerciseMeta>
                {bodyParts.length > 0 && (
                  <BodyPartTags>
                    {bodyParts.map((bp) => (
                      <BodyPartTag key={bp.id}>{bp.names.get(language) || bp.name}</BodyPartTag>
                    ))}
                  </BodyPartTags>
                )}
              </ExerciseInfo>
              <PlayHint className="play-hint">
                <i className="mdi mdi-play" />
              </PlayHint>
            </ExerciseItem>
          );
        })}
      </ExerciseList>
      <ExerciseVideoModal isOpen={isModalOpen} onClose={handleCloseModal} exercise={selectedExercise} />
    </Section>
  );
});
